import { capitalize } from "lodash";
import { rollbar } from "./ErrorBoundary/index.js";
import { customEvent } from "./gtag.js";

/** Firebase codes for a sign-in the user backed out of; not failures. */
export const SIGN_IN_CANCEL_CODES = [
  "auth/popup-closed-by-user",
  "auth/user-cancelled",
];

/** Firebase codes caused by the user, their browser or their connection; reported as warnings. */
export const USER_SIDE_SIGN_IN_ERROR_CODES = [
  ...SIGN_IN_CANCEL_CODES,
  "auth/account-exists-with-different-credential",
  "auth/cancelled-popup-request",
  "auth/expired-action-code",
  "auth/invalid-action-code",
  "auth/invalid-email",
  "auth/network-request-failed",
  "auth/popup-blocked",
  "auth/too-many-requests",
];

/**
 * Whether a sign-in error is the user cancelling at the provider.
 *
 * @param {{ code?: string } | undefined} err
 * @returns {boolean}
 */
export const isSignInCancel = (err) => SIGN_IN_CANCEL_CODES.includes(err?.code);

/**
 * Rollbar fingerprint grouping a Firebase sign-in failure by stage, code and
 * provider. Other errors get none, so they keep Rollbar's default grouping.
 *
 * @param {string} stage
 * @param {string} [code]
 * @param {string} [provider]
 * @returns {string | undefined}
 */
export const signInFingerprint = (stage, code, provider) => {
  if (typeof code !== "string" || !code.startsWith("auth/")) return undefined;
  return provider ? `signin:${stage}:${code}:${provider}` : `signin:${stage}:${code}`;
};

/**
 * Domain part of an email address, or null when there isn't one.
 *
 * @param {string} [email]
 * @returns {string | null}
 */
export const emailDomainOf = (email) =>
  typeof email === "string" && email.includes("@")
    ? email.split("@").pop().toLowerCase()
    : null;

/**
 * Reports a failed sign-in step to Rollbar under `[SignIn] <stage> failed`,
 * one item per stage, Firebase code and provider. Callers pass only
 * non-identifying context (domains, keys, provider names).
 *
 * @param {string} stage Step that failed, e.g. "signInWithPopup".
 * @param {{ code?: string, message?: string } | undefined} err
 * @param {Record<string, unknown>} [context]
 */
export function reportSignInError(stage, err, context = {}) {
  const level = USER_SIDE_SIGN_IN_ERROR_CODES.includes(err?.code)
    ? "warning"
    : "error";
  const rollbarFingerprint = signInFingerprint(stage, err?.code, context.provider);
  rollbar[level](`[SignIn] ${stage} failed`, err, {
    stage,
    code: err?.code,
    ...context,
    ...(rollbarFingerprint && { rollbarFingerprint }),
  });
}

/**
 * Tracks a failed or cancelled provider sign-in as the
 * `user_click_signin_<outcome>` and `user_signin_<outcome>_<provider>` GA events.
 *
 * @param {string} providerName e.g. "google".
 * @param {{ code?: string, message?: string }} err
 */
export function trackProviderSignInFailure(providerName, err) {
  const outcome = isSignInCancel(err) ? "cancelled" : "error";
  const label = `Sign in with ${capitalize(providerName)} ${capitalize(outcome)}`;
  const error = err?.code || err?.message;
  customEvent({
    name: `user_click_signin_${outcome}`,
    category: "Authorization",
    label,
    data: { provider: providerName, error },
  });
  customEvent({
    name: `user_signin_${outcome}_${providerName}`,
    category: "Authorization",
    label,
    data: { error, url: window.location.href },
  });
}
